import angular from 'angular';
import daterangeComponent from './daterange-component';
import dateComponent from '../date';
import dropdownComponent from '../dropdown';
import i18n from '../i18n';
import angularBootstrap from 'angular-ui-bootstrap';
import moment from 'moment-timezone';
import time from '../time';

/**
 * @ngdoc module
 * @name akamai.components.daterange
 *
 * @description
 * Daterange is an interactive calendar component that allows users to select range between a
 * start and end date. This is best used as a filtering mechanism (i.e. as a means to
 * filter a report).
 *
 * Timezones and Model Behavior
 *
 * To allow for the most flexibility, the model for the daterange component uses simple native
 * javascript date objects. Native javascript date objects always use the local timezone. If the
 * timezone attribute is supplied to the component, the component will allow users to change the
 * timezone. It will figure out the offset between the selected timezone and local timezone and
 * apply/update the model with the correct offset.
 *
 * Example:
 *
 * 1.) The user is in EST and has selected Jan 1, 12:00am to Jan 2, 12:00am EST in a daterange
 *     component
 * 2.) The user changes just the timezone to PST in the same daterange component. (The daterange
 *     component now reads Jan 1, 12:00am to Jan 2, 12:00am PST)
 * 3.) Since native javascript date objects are always in local time (in this example EST), the
 *     model needs to be updated accordingly. The following question needs to be answered:
 *     "Jan 1, 12:00am to Jan 2, 12:00am PST is what date/time in EST?" - the answer is
 *      Jan 1, 3:00am to Jan 2, 3:00am EST
 * 4.) The component still reads Jan 1, 12:00am to Jan 2, 12:00am PST (view-model) but the ngModel
 *     is now Jan 1, 3:00am to Jan 2, 3:00am EST
 *
 * A timezone can be initially supplied to the component via ng-model (see below). The list of
 * supported timezones can be found in permitted-timezones.json within akamai-core.
 *
 * Lastly, remember to always persist dates in UTC. These dates will be localized when javascript
 * date object representations are created from them. The daterange component is designed to work
 * with these localized javascript date objects.
 *
 * @guideline https://design.akamai.com/components/date-range-filter/
 *
 * @example date-range.html
 * <akam-daterange
 *   mode="day"
 *   ng-model="..."
 *   min="..."
 *   max="..."
 *   format="..."
 *   is-disabled="..."
 *   is-readonly="..."
 *   time
 *   timezone
 *   timezone-filtering
 *   apply-button
 *   no-meridian
 *   right
 *   from-placeholder"...""
 *   to-placeholder="..."
 *   range-filters="..."
 *   on-filter-change="..."
 *   custom-filter-not-filterable>
 * </akam-daterange>
 */
export default angular.module('akamai.components.daterange', [
  angularBootstrap,
  i18n.name,
  time.name,
  dateComponent.name,
  dropdownComponent.name
])
  /**
   * @ngdoc directive
   *
   * @name akamDaterange
   *
   * @description Creates a daterange control
   *
   * @restrict E
   *
   * @param {Object} ng-model The model for the component.
   * @param {Date} ng-model.from The starting date value. Must be a valid Javascript `Date` object.
   * @param {Date} ng-model.to The ending date value. Must be a valid Javascript `Date` object.
   * @param {string | number} ng-model.rangeId Id of selected range filter.
   * @param {string} [ng-model.timezone] A timezone to use for rendering of the control.
   * Possible timezone strings can be found in the `timezones` service (i.e. `timezones.data`).
   * { from: new Date(), to: new Date(), timezone: 'Europe/Moscow' }.
   *
   * @param {string} [mode="day"] Determines the display mode. Can be either `day` or `month`.
   *
   * @param {Date} min The earliest date users can select. Dates before this point are disabled.
   *
   * @param {Date} max The latest date users can select. Dates after this point are disabled.
   *
   * @param {string} [format="EEE, MMM dd, yyyy" | format="MMM yyyy"] An angular-compatible date
   * format.
   *
   * @param {boolean} [is-disabled=false] If disabled, no user interaction will be possible.
   *
   * @param {boolean} [is-readonly=false] If readonly, no user interaction will be possible but the
   * text will be easier to read.
   *
   * @param {*} [time] If present, user may select from and to times.
   *
   * @param {number} [hour-step=1] If time is present, this value is used to increment/decrement
   * the hour value.
   *
   * @param {number} [minute-step=15] If time is present, this value used to increment/decrement
   * the minute value.
   *
   * @param {*} [timezone] If present, user may select timezone.
   * If present, daterange will render time control for from and to dates.
   *
   * @param {*} [timezone-label] If present, daterange will render timezone label next to dates.
   *
   * @param {*} [filter-timezones] If present, list of timezones will be filtered.
   * For ranges shorter than 1day, all timezones are shown.
   * For ranges longer than 1 but shorter than 30days, only full-hour-offset timezones are shown.
   * For ranges longer than 30days only GMT timezone is shown.
   *
   * @param {*} [apply-button] If present, apply and cancel buttons will be shown.
   * Apply button only triggers the "blur" event, use ng-model-options="{updateOn: 'blur'}"
   * to make update of the model only happen after apply button is clicked.
   *
   * @param {string | TranslateKey} [apply-button-text] Text to display on the "Apply" button.
   *
   * @param {string | TranslateKey} [cancel-button-text] Text to display on the "Cancel" button.
   *
   * @param {*} [no-meridian] If present, time will be in 24 hour format.
   *
   * @param {*} [right] If present, the daterange will be rendered on the right.
   *
   * @param {string | TranslateKey} [from-placeholder] The placeholder text for the starting date.
   *
   * @param {string | TranslateKey} [to-placeholder] The placeholder text for the ending date.
   *
   * @param {Array | *} [range-filters] If present and has Predefined range filters
   * configuration object data, then the Predefined range filter list will be displayed
   * Example of array object: [{id: 123, label: 'abc or locale key', selected: true},...]
   *
   * @param {Function} [on-filter-change] A callback function takes filter Id value,
   * and return date range object as {from: dateObject, to: dateObject}
   *
   * @param {*} [custom-range-not-filterable] If present, custom range filter will be disabled
   * and return date range object as {from: dateObject, to: dateObject}
   *
   * @param {Function} dateDisabled An optional expression to disable visible options based on
   * passing an object with date and current mode properties
   *
   * @param {*} [from-options] Options for datepicker associated with start date
   *
   * @param {*} [to-options] Options for datepicker associated with end date
   */
  .component('akamDaterange', daterangeComponent)
  .constant('FROM_EVENT_NAMESPACE', 'from')
  .constant('TO_EVENT_NAMESPACE', 'to')
  .value('moment', moment);
